import {
	ATTRIBUTES,
	openBackInStockModal,
	PUB_SUB_EVENTS,
	SELECTORS,
	createATCitems
} from '@/scripts/core/global'
import { TsDOM as q } from '@/scripts/core/TsDOM'
import { publish } from '@/scripts/core/global'
import { QuickAddModal } from '@/scripts/optional/quick-add'
import { UcoastEl } from '@/scripts/core/UcoastEl'
import {
	applyStoqPreorderProperties,
	getStoqShippingTextInputValue,
	syncStoqSellingPlanInput,
} from '@/scripts/product/stoq-selling-plan'
import {
	addItemsToCart,
	getCartAttributesFromFormData,
	getDOMCartSectionApiIds, hasDomCart,
	renderResponseToCartDrawer
} from '@/scripts/core/cart-functions';
import { CartErrorResponse } from '@/scripts/shopify'
import { openPersonalizationPopup } from '@/scripts/core/global'

export class ProductForm extends UcoastEl {
	static htmlSelector = 'product-form'
	static selectors = {
		form: 'form',
		formIdEl: '[name=id]',
		submitButton: '.product-form__submit, button[type="submit"][name="add"]',
		submitButtonMessage: '[data-uc-inventory-status-text], span',
		soldOutMessage: '[data-uc-sold-out-message]',
		errorMessageWrapper: '[data-uc-product-form-error-message-wrapper]',
		errorMessage: '[data-uc-product-form-error-message]',
		triggerPersonalizationPopup: '.trigger-personalization-popup',
		quantityInput: '[name="quantity"]',
		itemQuantityInput: '[data-uc-product-item-quantity]',
		upsellCheckbox: '[data-uc-product-upsell-checkbox]',
		upsellLineInput: '[data-uc-product-upsell-line-input]',
		upsellQuantityInput: '[data-uc-product-upsell-quantity]',
		upsellParentIdInput: '[data-uc-product-upsell-parent-id]',
	}

	form: HTMLFormElement
	formIdEl: HTMLInputElement
	submitButton: HTMLButtonElement
	triggerPersonalizationButton?: HTMLButtonElement
	quantityInput?: HTMLInputElement
	itemQuantityInput?: HTMLInputElement
	upsellCheckbox?: HTMLInputElement
	upsellLineInputs: HTMLInputElement[] = []
	upsellQuantityInput?: HTMLInputElement
	upsellParentIdInputs: HTMLInputElement[] = []
	errorMessageWrapper?: HTMLElement
	errorMessage?: HTMLElement
	hideErrors: boolean
	error?: boolean
	nativeSubmit: HTMLFormElement['submit']

	constructor() {
		super()

		this.form = q.rs(ProductForm.selectors.form, this)
		this.formIdEl = (this.form.querySelector('[name="id"]') as HTMLInputElement) || (this.form.querySelector('[name="items[0][id]"]') as HTMLInputElement)
		this.triggerPersonalizationButton = this.querySelector(ProductForm.selectors.triggerPersonalizationPopup) as HTMLButtonElement
		this.quantityInput = this.getQuantityInput()
		this.itemQuantityInput = this.getFormAssociatedInput(ProductForm.selectors.itemQuantityInput)
		this.upsellCheckbox = this.getFormAssociatedInput(ProductForm.selectors.upsellCheckbox)
		this.upsellLineInputs = this.getFormAssociatedInputs(ProductForm.selectors.upsellLineInput)
		this.upsellQuantityInput = this.getFormAssociatedInput(ProductForm.selectors.upsellQuantityInput)
		this.upsellParentIdInputs = this.getFormAssociatedInputs(ProductForm.selectors.upsellParentIdInput)
		this.formIdEl.disabled = false

		this.submitButton = q.rs(ProductForm.selectors.submitButton, this)
		this.nativeSubmit = this.form.submit.bind(this.form)
		this.form.submit = this.onProgrammaticSubmit.bind(this) as HTMLFormElement['submit']
		this.form.addEventListener('submit', this.onSubmitHandler.bind(this))
		this.submitButton.addEventListener('click', this.onClickOos.bind(this))
		this.quantityInput?.addEventListener('change', this.syncLineItemState.bind(this))
		this.quantityInput?.addEventListener('input', this.syncLineItemState.bind(this))
		this.upsellCheckbox?.addEventListener('change', this.syncLineItemState.bind(this))
		if (hasDomCart()) {
			this.submitButton.setAttribute('aria-haspopup', 'dialog')
		}

		this.hideErrors = this.dataset.hideErrors === 'true'
		syncStoqSellingPlanInput({
			form: this.form,
			variantId: this.formIdEl.value,
			preorder: this.submitButton.dataset.preorder == 'true',
		})

		if(this.triggerPersonalizationButton) this.triggerPersonalizationButton.addEventListener('click', this.triggerPersonalization.bind(this))
		this.syncLineItemState()
	}

	override disconnectedCallback(): void {
		this.form.submit = this.nativeSubmit
		super.disconnectedCallback()
	}

	triggerPersonalization(e: Event) {
		const target = e.currentTarget as HTMLElement
		if(!target) return
		if(target.classList.contains('disabled')) return
		const id = String(this.formIdEl.value)
		const productUrl = String(this.dataset.url)
		openPersonalizationPopup(productUrl, id)
	}

	getSpinner() {
		return q.rs(SELECTORS.loadingOverlaySpinner, this)
	}

	onProgrammaticSubmit() {
		if (this.submitButton.getAttribute('aria-disabled') === 'true') return
		this.form.dispatchEvent(new Event('submit', { bubbles: true, cancelable: true }))
	}

	getQuantityInput(): HTMLInputElement | undefined {
		const formQuantityInput = this.form.querySelector<HTMLInputElement>(
			ProductForm.selectors.quantityInput
		)
		if (formQuantityInput) return formQuantityInput
		const formId = this.getFormId()
		if (!formId) return undefined
		return document.querySelector<HTMLInputElement>(
			`input[form="${CSS.escape(formId)}"][name="quantity"]`
		) ?? undefined
	}

	getFormAssociatedInput(selector: string): HTMLInputElement | undefined {
		const formInput = this.form.querySelector<HTMLInputElement>(selector)
		if (formInput) return formInput
		const formId = this.getFormId()
		if (!formId) return undefined
		return document.querySelector<HTMLInputElement>(
			`${selector}[form="${CSS.escape(formId)}"]`
		) ?? undefined
	}

	getFormAssociatedInputs(selector: string): HTMLInputElement[] {
		const formInputs = Array.from(this.form.querySelectorAll<HTMLInputElement>(selector))
		const formId = this.getFormId()
		if (!formId) return formInputs
		const externalInputs = Array.from(document.querySelectorAll<HTMLInputElement>(
			`${selector}[form="${CSS.escape(formId)}"]`
		))
		return [...formInputs, ...externalInputs]
	}

	getFormId() {
		return this.form.getAttribute('id') || ''
	}

	syncLineItemState() {
		this.upsellCheckbox = this.getFormAssociatedInput(ProductForm.selectors.upsellCheckbox)
		this.upsellLineInputs = this.getFormAssociatedInputs(ProductForm.selectors.upsellLineInput)
		this.upsellQuantityInput = this.getFormAssociatedInput(ProductForm.selectors.upsellQuantityInput)
		this.upsellParentIdInputs = this.getFormAssociatedInputs(ProductForm.selectors.upsellParentIdInput)

		const quantity = this.quantityInput?.value || this.itemQuantityInput?.value || '1'
		if (this.itemQuantityInput) this.itemQuantityInput.value = quantity
		if (this.upsellQuantityInput) this.upsellQuantityInput.value = quantity
		for (const input of this.upsellParentIdInputs) {
			input.value = this.formIdEl.value
		}

		if (!this.upsellCheckbox) return
		const upsellSelected = this.upsellCheckbox.checked
		for (const input of this.upsellLineInputs) {
			input.disabled = !upsellSelected
		}
	}

	onClickOos() {
		if (this.submitButton.getAttribute('aria-disabled') === 'true') return
		const bisVariantId = q.oa(this.submitButton, 'data-uc-bis-variant-id')
		const oosUrl = q.oa(this.submitButton, 'data-oos-popup-trigger')
		if (bisVariantId || oosUrl) {
			openBackInStockModal({
				opener: this.submitButton,
				klaviyoUrl: oosUrl,
				variantId: bisVariantId,
			})
		}
	}

	async onSubmitHandler(event: Event) {
		event.preventDefault()
		if (this.submitButton.getAttribute('aria-disabled') === 'true') return
		this.handleErrorMessage()
		this.syncLineItemState()

		const preorder = this.submitButton.dataset.preorder == 'true'
		syncStoqSellingPlanInput({
			form: this.form,
			variantId: this.formIdEl.value,
			preorder,
		})
		this.submitButton.setAttribute('aria-disabled', 'true')
		this.submitButton.setAttribute(ATTRIBUTES.loading, '')
		this.getSpinner().classList.remove('hidden')

		const formData = new FormData(this.form)
		this.dispatchEvent(
			new CustomEvent('uc:product-form:before-submit', {
				bubbles: true,
				detail: {
					formData,
				},
			})
		)
		let formVariantId = formData.get('id') || formData.get('items[0][id]')
		if (!formVariantId) throw Error('No variant id found')

		if(this.submitButton.id == 'cart-note-form-submit') {
			const freeItems = document.querySelectorAll<HTMLElement>('.cart-item[data-free-product]')
			const updates: Record<string, number> = {}
			freeItems.forEach(item => {
				const key = item.dataset.key || item.dataset.variantId;
				if (key) updates[key] = 0;
			})
			await fetch('/cart/update.js', {
				method: 'POST',
				headers: {
					'Content-Type': 'application/json'
				},
				body: JSON.stringify({ updates })
			})
		}
		const addedVariantId = parseInt(formVariantId.toString())

		type AddToCartItem = {
			id: number
			quantity: number
			selling_plan?: number
			properties: Record<string, string>
			parent_id?: number
		}
		let items: AddToCartItem[] = []
		const hideCartLineItemDetails =
			formData.get('items[0][properties[_hide_cart_line_item_props]]') === 'true'

		if(formData.get('items[0][id]')) {
			if (!hideCartLineItemDetails) {
				applyStoqPreorderProperties({
					formData,
					preorder,
					shippingText: getStoqShippingTextInputValue(this.form),
				})
			}
			items = createATCitems(formData) as AddToCartItem[]
		} else {
			if (!hideCartLineItemDetails) {
				applyStoqPreorderProperties({
					formData,
					preorder,
					shippingText: getStoqShippingTextInputValue(this.form),
				})
			}
		}
		const cartAttributes = getCartAttributesFromFormData(formData)

		addItemsToCart(
			items.length ? items : formData,
			getDOMCartSectionApiIds(),
			cartAttributes
		)
			.then((cart) => {
				if ('status' in cart) {
					this.handleError(cart, addedVariantId)
					return
				}

				publish(PUB_SUB_EVENTS.cartUpdate, {
					source: 'product-form',
					productVariantId: addedVariantId,
				})
				const quickAddModal = q.oc<QuickAddModal>(
					this,
					'quick-add-modal'
				)
				renderResponseToCartDrawer(cart, quickAddModal)
			})
			.catch((e) => {
				console.error(e)
			})
			.finally(() => {
				this.submitButton.removeAttribute(ATTRIBUTES.loading)

				if (!this.error)
					this.submitButton.removeAttribute('aria-disabled')
				this.getSpinner().classList.add('hidden')
			})
	}

	handleError(cart: CartErrorResponse, addedVariantId: number) {
		publish(PUB_SUB_EVENTS.cartError, {
			source: 'product-form',
			productVariantId: addedVariantId,
			message: cart.message,
			errors: cart.errors ?? cart.description,
		})
		this.handleErrorMessage(cart.description)

		const soldOutMessage = this.submitButton.querySelector(
			ProductForm.selectors.soldOutMessage
		)
		if (!soldOutMessage) return
		this.submitButton.setAttribute('aria-disabled', 'true')
		const submitButtonText = q.rs(
			ProductForm.selectors.submitButtonMessage,
			this.submitButton
		)
		submitButtonText.classList.add('hidden')
		soldOutMessage.classList.remove('hidden')
	}

	handleErrorMessage(errorMessage?: string) {
		if (this.hideErrors) return

		this.errorMessageWrapper =
			this.errorMessageWrapper ||
			q.rs(ProductForm.selectors.errorMessageWrapper, this)
		if (!this.errorMessageWrapper)
			throw new Error('No error message wrapper found')
		this.errorMessage =
			this.errorMessage ||
			q.rs(
				ProductForm.selectors.errorMessage,
				this.errorMessageWrapper
			)

		this.errorMessageWrapper.toggleAttribute('hidden', !errorMessage)

		if (errorMessage) {
			this.errorMessage.textContent = errorMessage
		}
	}
}
